import Section from 'components/layouts/Section';
import i18n from 'i18n';
import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LandmarkDto } from 'services/data-contracts';
import ServiceHelper from 'stores/ServiceHelper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/navigation';
import { Scrollbar, Navigation } from 'swiper/modules';
import { useStoreWrapper } from 'stores/StoreHelper';
import LandmarkCard from './LandmarkCard';
import LandmarkModal from './LandmarkModal';
import AllLandmarkModal from './AllLandmarkModal';
import { useMediaContext } from 'saladsoft-primitive';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styled from 'styled-components';

const NearBy = ({ listingId }: { listingId?: string }) => {
  const { t } = useTranslation();
  const { uiStore } = useStoreWrapper();
  const { isMobile } = useMediaContext();
  const [landmarks, setLandmarks] = useState<LandmarkDto[]>([]);

  const uniqueId = useId().replace(/:/g, '');
  const prevBtnId = `swiper-prev-${uniqueId}`;
  const nextBtnId = `swiper-next-${uniqueId}`;

  const fetchNearBy = (listingId: number) => {
    ServiceHelper.listingService
      ?.getLandmarksByListing(listingId, {
        locale: i18n.language,
      })
      .then((respont) => {
        if (respont) {
          setLandmarks(respont.data);
        }
      });
  };

  const handleClick = (landmark?: LandmarkDto) => {
    {
      if (isMobile) {
        uiStore?.universalModal.show({
          title: landmark?.name,
          modalOption: { style: { width: 600, height: '100%', maxHeight: '80vh' } },
          children: <LandmarkModal landmark={landmark} />,
        });
      } else {
        uiStore?.modal.show({
          title: landmark?.name,
          children: <LandmarkModal landmark={landmark} />,
          style: {
            width: 600,
            height: '100%',
            maxHeight: '80vh',
          },
        });
      }
    }
  };

  const handleShowAllLandmark = () => {
    const mobileStyle = {
      width: '100%',
      height: '100%',
      maxHeight: '100%',
    };
    const pcStyle = {
      width: 600,
      height: '100%',
      maxHeight: '80vh',
    };
    uiStore?.modal.show({
      title: t('NearBy'),
      children: <AllLandmarkModal landmarks={landmarks} />,
      style: {
        ...(isMobile ? mobileStyle : pcStyle),
      },
    });
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (listingId) {
      const listingIdNumber = parseInt(listingId, 10);
      if (listingIdNumber) {
        fetchNearBy(listingIdNumber);
      }
    }
  }, [listingId]);

  return (
    <Section title={t('NearBy')} rightAction={<></>}>
      <SwiperWrapper>
        <Swiper
          spaceBetween={10}
          slidesPerView="auto"
          scrollbar={{
            hide: true,
          }}
          navigation={{
            prevEl: `#${prevBtnId}`,
            nextEl: `#${nextBtnId}`,
          }}
          modules={[Scrollbar, Navigation]}
          className={`mySwiper-${uniqueId}`}
          loop={true}
        >
          {landmarks?.map((landmark) => (
            <SwiperSlide key={landmark.id} style={{ width: 'auto' }}>
              <div
                style={{
                  width: 140,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
              >
                <LandmarkCard
                  landmark={landmark}
                  onClick={() => {
                    handleClick(landmark);
                  }}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <NavButton
          id={prevBtnId}
          className="swiper-button-prev"
          position="left"
          onClick={handleButtonClick}
        >
          <NavIcon>
            <StyledChevronLeftIcon />
          </NavIcon>
        </NavButton>
        <NavButton
          id={nextBtnId}
          className="swiper-button-next"
          position="right"
          onClick={handleButtonClick}
        >
          <NavIcon>
            <StyledChevronRightIcon />
          </NavIcon>
        </NavButton>
      </SwiperWrapper>
      {landmarks?.length === 0 && (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <div>{t('NoLandmarks')}</div>
        </div>
      )}
    </Section>
  );
};

const SwiperWrapper = styled.div`
  position: relative;
  z-index: 0;
  .swiper-scrollbar {
    width: 80%;
    bottom: 20px !important;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover .swiper-scrollbar {
    opacity: 1;
  }
`;

const NavButton = styled.div<{ position: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ position }) => (position === 'left' ? 'left: 15px;' : 'right: 15px;')}
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  opacity: 1 !important;
  visibility: visible;
  transition: background-color 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  padding: 0;
  box-sizing: border-box;

  &:hover {
    background-color: rgba(255, 255, 255, 0.9);
  }

  @media ${(props) => props.theme.media.mobile} {
    display: none;
  }
`;

const NavIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const StyledChevronLeftIcon = styled(ChevronLeftIcon)`
  font-size: 24px;
  color: #333;
`;

const StyledChevronRightIcon = styled(ChevronRightIcon)`
  font-size: 24px;
  color: #333;
`;

export default NearBy;
